import { FunctionComponent, useState } from "react";
import { useTranslation } from "react-i18next";

import {
	faChevronDown,
	faCoins,
	faGlobe,
	faInfoCircle,
	faSliders,
	faUserPlus,
} from "@fortawesome/pro-regular-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

import { Icon } from "@/components";
import { classNames } from "@/utils/classNames";

import { BillingMode } from "./types";

interface AddOnsProps {
	mode: BillingMode;
}

interface AddOnRow {
	icon: IconDefinition;
	name: string;
	desc: string;
	price: string;
	sub: string;
}

/**
 * Informational, collapsible "Customize your plan" section. Add-ons are bought
 * later from workspace settings — shown here for reference only (no purchasing).
 */
export const AddOns: FunctionComponent<AddOnsProps> = ({ mode }) => {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const isAnnual = mode === "annual";

	const rows: AddOnRow[] = [
		{
			icon: faCoins,
			name: t("usage.subscription.checkout.addons.topup.name"),
			desc: t("usage.subscription.checkout.addons.topup.desc"),
			price: t("usage.subscription.checkout.addons.topup.price"),
			sub: t("usage.subscription.checkout.addons.topup.sub"),
		},
		{
			icon: faUserPlus,
			name: t("usage.subscription.checkout.addons.senders.name"),
			desc: t("usage.subscription.checkout.addons.senders.desc"),
			price: isAnnual
				? t("usage.subscription.checkout.addons.senders.priceAnnual")
				: t("usage.subscription.checkout.addons.senders.priceMonthly"),
			sub: isAnnual
				? t("usage.subscription.checkout.addons.senders.subAnnual")
				: t("usage.subscription.checkout.addons.senders.subMonthly"),
		},
		{
			icon: faGlobe,
			name: t("usage.subscription.checkout.addons.scrape.name"),
			desc: t("usage.subscription.checkout.addons.scrape.desc"),
			price: isAnnual
				? t("usage.subscription.checkout.addons.scrape.priceAnnual")
				: t("usage.subscription.checkout.addons.scrape.priceMonthly"),
			sub: t("usage.subscription.checkout.addons.scrape.sub"),
		},
	];

	return (
		<div className="w-full bg-white border border-border-light rounded-16 overflow-hidden">
			<button
				aria-expanded={open}
				className="flex items-center gap-3 w-full p-4 text-left"
				type="button"
				onClick={() => setOpen((prev) => !prev)}
			>
				<span className="flex items-center justify-center w-[34px] h-[34px] rounded-lg bg-blue-action-200 text-blue-action flex-shrink-0">
					<Icon className="w-[18px] h-[18px]" icon={faSliders} />
				</span>
				<span className="flex-1">
					<span className="block text-ssm font-semibold text-text">
						{t("usage.subscription.checkout.addons.title")}
					</span>
					<span className="block text-sm text-text-meta mt-0.5">
						{t("usage.subscription.checkout.addons.subtitle")}
					</span>
				</span>
				<Icon
					className={classNames(
						"w-4 h-4 text-text-meta transition-transform",
						open ? "rotate-180" : ""
					)}
					icon={faChevronDown}
				/>
			</button>

			{open && (
				<div className="px-4 pb-4">
					<div className="flex gap-2.5 items-start bg-background-light rounded-lg p-3 text-sm text-text-meta mb-2">
						<Icon className="w-4 h-4 flex-shrink-0 mt-0.5" icon={faInfoCircle} />
						<span>{t("usage.subscription.checkout.addons.note")}</span>
					</div>

					{rows.map((row) => (
						<div
							key={row.name}
							className="flex items-center gap-3.5 py-3.5 border-t border-divider first:border-t-0"
						>
							<span className="flex items-center justify-center w-[30px] h-[30px] rounded-lg bg-background-light text-text-meta flex-shrink-0">
								<Icon className="w-4 h-4" icon={row.icon} />
							</span>
							<span className="flex-1 min-w-0">
								<span className="block text-sm font-semibold text-text">{row.name}</span>
								<span className="block text-xs text-text-meta mt-0.5 leading-relaxed">
									{row.desc}
								</span>
							</span>
							<span className="text-right flex-shrink-0">
								<span className="block text-sm font-semibold text-text">{row.price}</span>
								<span className="block text-xs text-text-meta mt-0.5">{row.sub}</span>
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
};
